"use client"

/**
 * Which job the Add questions page is built around. The two versions are not
 * two skins on one flow; they start from opposite ends of the work.
 *
 * **Assemble** (version 1) treats the section as empty and the bank as the
 * subject: search, filter, recommend, add. It suits a paper being written for
 * the first time.
 *
 * **Adapt** (version 2) treats last term's paper as the starting draft and the
 * course objectives as the thing to answer to: copy the paper, reword what
 * students may have seen, drop the topics this offering did not reach, and top
 * up the ones it did. Faculty reported this is what they actually do, and on
 * version 1 it is the slowest path on the page.
 *
 * Set from the profile menu, like `use-question-search-mode` and
 * `use-home-variant`, so the two can be compared without a switch spending the
 * page's most valuable row. Read by the page and set from two profile menus, so
 * it needs in-document sync: hence the same small external store rather than
 * `usePersistedState`, which only notifies other browser tabs.
 *
 * "Journey" rather than "version" in the types because `QuestionVersion`
 * already means a question's wording revisions in this codebase.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

export type AddQuestionsJourney = "assemble" | "adapt"

export const ADD_QUESTIONS_JOURNEYS: {
  id: AddQuestionsJourney
  /** What the profile menu shows. Numbered, because the ask was two versions. */
  label: string
  description: string
}[] = [
  {
    id: "assemble",
    label: "Version 1",
    description: "Build from the bank. Search, recommend, add one at a time",
  },
  {
    id: "adapt",
    label: "Version 2",
    description:
      "Start from a past paper. Reword, retopic, and check it against the course objectives",
  },
]

const STORAGE_KEY = "add-questions-journey"
const VERSION = 1
const DEFAULT_JOURNEY: AddQuestionsJourney = "assemble"

const listeners = new Set<() => void>()
let snapshot: AddQuestionsJourney | null = null
let hydrated = false

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function isAddQuestionsJourney(value: unknown): value is AddQuestionsJourney {
  return value === "assemble" || value === "adapt"
}

function read(): AddQuestionsJourney {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION &&
        isAddQuestionsJourney((parsed as { d?: unknown }).d)
      ) {
        snapshot = (parsed as { d: AddQuestionsJourney }).d
        return snapshot
      }
    } catch {
      // Unparseable payload. Fall through to the default.
    }
  }

  snapshot = DEFAULT_JOURNEY
  return snapshot
}

export function setAddQuestionsJourney(next: AddQuestionsJourney) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useAddQuestionsJourney(): AddQuestionsJourney {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_JOURNEY)
}
